import type { Column } from "@tanstack/react-table"
import { CheckIcon, FileImage, FileSpreadsheet, ListFilter, Mail, Settings } from "lucide-react"
import * as React from "react"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import { FilePdfIcon, FilePowerpointIcon, FileWordIcon, MailAttachmentIcon } from "@/components/ui/icons"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useTagEditor } from "@/context/TagEditorContext"
import { cn } from "@/lib/utils"
import { getTagColorMap } from "./column-utilities"
import { useDialogContainer } from "./dialog-container-context"

interface DataTableFacetedFilterProps<TData, TValue> {
  column: Column<TData, TValue>
  title?: string
  options?: {
    label: string
    value: string
  }[]
  hideSettings?: boolean
  tagColors?: Record<string, string>
}

export function DataTableFacetedFilter<TData, TValue>({
  column,
  title,
  options,
  hideSettings = false,
  tagColors,
}: DataTableFacetedFilterProps<TData, TValue>) {
  const [open, setOpen] = React.useState(false)
  const dialogContainer = useDialogContainer();

  let facets = column?.getFacetedUniqueValues()
  const { openTagEditor } = useTagEditor()

  if (title === 'Tags' || title === 'Attachments' || title === 'To' || title === 'From' || title === 'File type') {
    const customFacets = new Map()

    for (const [key, value] of facets as any) {
      // Some rows (e.g. inline folder rows) have no array value for these columns, so the
      // faceted key is undefined/non-iterable — skip them rather than crashing on `for..of`.
      if (key == null || typeof (key as { [Symbol.iterator]?: unknown })[Symbol.iterator] !== 'function') continue;
      for (const k of key) {
        // For Attachments, extract fileName from Document object
        const facetKey = title === 'Attachments' ? k.fileName : k;
        const prevValue = customFacets.get(facetKey) || 0;
        customFacets.set(facetKey, prevValue + value);
      }
    }
    facets = customFacets;
  }

  const selectedValues = new Set(column?.getFilterValue() as string[])
  const filterOptions = options || Array.from(facets.keys()).sort().map(value => ({
    label: String(value),
    value: String(value)
  }));

  return (
    <div className="flex items-center">
      <Popover
        open={open}
        onOpenChange={(isOpen) => {
          // Only allow opening via trigger, closing is handled by click outside
          if (isOpen) setOpen(true);
        }}
        modal={true}
      >
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="relative w-6 h-8">
            <ListFilter className="h-4 w-4" />
            {selectedValues.size > 0 &&
              <span className="text-sm absolute -right-1 top-0 text-black/50">{selectedValues.size}</span>
            }
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[270px] p-0"
          align="start"
          container={dialogContainer}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={() => setOpen(false)}
          onPointerDownOutside={() => setOpen(false)}
        >
          <Command>
            <CommandInput 
              placeholder={title}
            />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup className="max-h-64 overflow-auto custom-scrollbar">
                {filterOptions.map((option) => {
                  const isSelected = selectedValues.has(option.value)
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => {
                        if (isSelected) {
                          selectedValues.delete(option.value)
                        } else {
                          selectedValues.add(option.value)
                        }
                        const filterValues = Array.from(selectedValues)
                        column?.setFilterValue(
                          filterValues.length ? filterValues : undefined
                        )
                      }}
                      onPointerDown={(e) => e.preventDefault()}
                    >
                      <div
                        className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-xs border border-primary",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible"
                        )}
                      >
                        <CheckIcon className={cn("h-4 w-4")} />
                      </div>
                      <div className="flex items-center">
                      {title === 'File type' && option.value === 'PDF' && <FilePdfIcon className="h-4 w-4 mr-2 text-red-600" />}
                      {title === 'File type' && option.value === 'Word' && <FileWordIcon className="h-4 w-4 mr-2 text-blue-700" />}
                      {title === 'File type' && option.value === 'PowerPoint' && <FilePowerpointIcon className="h-4 w-4 mr-2 text-orange-700" />}
                      {title === 'File type' && option.value === 'Excel' && <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-700" />}
                      {title === 'File type' && option.value === 'Image' && <FileImage className="h-4 w-4 mr-2 text-sky-700" />}
                      {title === 'File type' && option.value === 'Email' && <Mail className="h-4 w-4 mr-2 text-gray-500" />}
                      {title === 'File type' && option.value === 'Email with attachments' && (
                        <div className="mr-2">
                          <MailAttachmentIcon className="h-4 w-4 text-gray-500" />
                        </div>
                      )}
                      <span
                        className={title === 'Attachments' ? 'text-xs truncate max-w-[180px]' : ''}
                        title={title === 'Attachments' ? option.label : undefined}
                      >{option.label}</span>
                      </div>
                      {title === 'Tags' && (
                        <div
                          className="w-4 h-4 rounded-xs"
                          style={{ backgroundColor: tagColors?.[option.value] || getTagColorMap()[option.value] || '#CCCCCC' }}
                        >
                        </div>
                      )}
                      {!options && facets?.get(option.value) && title !== 'Attachments' && (
                        <span className="ml-auto flex h-4 w-4 items-center justify-center text-xs">
                          {facets.get(option.value)}
                        </span>
                      )}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
              {selectedValues.size > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => column?.setFilterValue(undefined)}
                      onPointerDown={(e) => e.preventDefault()}
                      className="justify-center text-center"
                    >
                      Clear filters
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      
      {title === 'Tags' && !hideSettings && (
        <Button
          variant="ghost"
          size="sm"
          className="ml-0.5 h-8 w-6 p-0"
          onClick={openTagEditor}
          title="Manage Tags"
        >
          <Settings className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
